import {
  Button,
  CircularProgress,
  DialogActions,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useContext, useState } from "react";
import * as React from "react";

import useUpdateCategory from "../../../hooks/mutation/category/use-update-category";
import useText from "../../../hooks/use-text";
import { CategoryContext } from "../../../providers/category-provider";
import { LocalizedStrings, SupportedLanguages } from "../../../utils/lang";

type Props = {
  isOpen: boolean;
  categoryId: string;
  onClose(): void;
};

export default function EditCategoryDialog(props: Props): ReactElement {
  const { isOpen, categoryId, onClose } = props;

  // Note(john): Workaround to get the category in the tree view
  const { categories } = useContext(CategoryContext);
  const targetCategory = categories.find((v) => v.id === categoryId);

  const [name, setName] = useState(targetCategory?.name || "");
  const [order, setOrder] = useState(targetCategory?.orderIndex);

  const { strings } = useText(local);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updateCategory } = useUpdateCategory();

  async function onSave(): Promise<void> {
    setIsSubmitting(true);
    try {
      await updateCategory(categoryId, {
        name: name,
        orderIndex: order,
      });
      onClose();
      enqueueSnackbar(strings.info.success, { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(strings.info.failed, { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  function onIndexChange(value: string): void {
    if (value < "1") {
      setOrder(1);
    } else {
      setOrder(parseInt(value));
    }
  }

  if (!targetCategory) {
    return <></>;
  }

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>{strings.title}</DialogTitle>
      <DialogContent className="min-w-[300px]">
        <TextField
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
          autoFocus
          focused
          margin="dense"
          id="name"
          type="text"
          fullWidth
          placeholder={strings.form.name.placeholder}
          variant="standard"
          value={name}
        />
        <TextField
          onChange={(e) => onIndexChange(e.target.value)}
          margin="dense"
          id="name"
          label={strings.form.order.label}
          type="number"
          variant="standard"
          value={order}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{strings.abort}</Button>
        <Button
          disabled={!name || name.length < 3 || isSubmitting}
          onClick={onSave}
        >
          {isSubmitting ? (
            <Box className="flex w-full justify-center">
              <CircularProgress size="1rem" className="w-1 h-1" />
            </Box>
          ) : (
            strings.save
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type LocalSchema = {
  title: string;
  form: {
    name: {
      placeholder: string;
    };
    order: {
      label: string;
    };
  };
  info: {
    success: string;
    failed: string;
  };
  save: string;
  abort: string;
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    title: "Edit category",
    form: {
      name: {
        placeholder: "Enter category name",
      },
      order: {
        label: "Order index",
      },
    },
    info: {
      success: "Category updated",
      failed: "Error occured during the category update. Please try it again",
    },
    save: "Save",
    abort: "Close",
  },
};
